import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import PromoBanner from "@/components/PromoBanner";
import { Button } from "@/components/ui/button";
import { Radio, Users, Copy, ArrowRight, Zap, Trophy } from "lucide-react";

export default function RaceLobby() {
  const nav = useNavigate();
  const [joinCode, setJoinCode] = useState("");
  const [passages, setPassages] = useState([]);
  const [selectedPassage, setSelectedPassage] = useState("");
  const [openRaces, setOpenRaces] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/typing-passages").then((r) => {
      setPassages(r.data.slice(0, 40));
      if (r.data[0]) setSelectedPassage(r.data[0].id);
    }).catch(() => {});
    api.get("/races/open/list").then((r) => setOpenRaces(r.data.races || [])).catch(() => {});
  }, []);

  const create = async () => {
    if (!selectedPassage) return toast.error("Choose a passage");
    setBusy(true);
    try {
      const { data } = await api.post("/races", { passage_id: selectedPassage });
      toast.success(`Race created — share code ${data.code}`);
      nav(`/race/${data.code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create race");
    } finally { setBusy(false); }
  };

  const join = async (code) => {
    const c = (code || joinCode).trim().toUpperCase();
    if (c.length !== 6) return toast.error("Enter a 6-character code");
    setBusy(true);
    try {
      await api.post(`/races/${c}/join`);
      nav(`/race/${c}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to join");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PromoBanner />
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-crimson mb-3 flex items-center gap-2">
            <Radio className="w-3.5 h-3.5" /> Multiplayer · Live
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight" data-testid="race-lobby-title">
            Race. <span className="text-brand-gold">Type fast.</span> Win.
          </h1>
          <p className="mt-3 text-lg text-muted-foreground max-w-2xl">
            Up to 10 racers on the same passage. Live leaderboard updates every second. Fastest correct typist wins the gold medal.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Create race */}
          <div className="border border-brand-gold/40 bg-brand-gold/5 rounded-2xl p-8" data-testid="race-create-panel">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-2">Host a room</div>
            <h2 className="font-display text-2xl font-black tracking-tight mb-4">Create a new race</h2>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Passage</label>
            <select
              value={selectedPassage}
              onChange={(e) => setSelectedPassage(e.target.value)}
              className="w-full h-11 px-3 bg-secondary border border-border rounded-lg font-mono text-sm text-foreground mb-5"
              data-testid="race-passage-select"
            >
              {passages.map((p) => (
                <option key={p.id} value={p.id}>{p.title} · {p.word_count || "-"} words · {p.language?.toUpperCase() || "EN"}</option>
              ))}
            </select>
            <Button onClick={create} disabled={busy} className="w-full h-11 rounded-lg bg-brand-gold hover:bg-brand-gold/90 text-background text-xs uppercase tracking-[0.2em] font-black" data-testid="race-create-btn">
              <Zap className="w-4 h-4 mr-2" /> Create race
            </Button>
          </div>

          {/* Join race */}
          <div className="border border-brand-blue/40 bg-brand-blue/5 rounded-2xl p-8" data-testid="race-join-panel">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-blue mb-2">Join a room</div>
            <h2 className="font-display text-2xl font-black tracking-tight mb-4">Have a race code?</h2>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">6-character code</label>
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
              placeholder="ABC123"
              maxLength={6}
              className="w-full h-11 px-3 bg-secondary border border-border rounded-lg font-mono text-lg text-foreground tracking-widest mb-5"
              data-testid="race-code-input"
            />
            <Button onClick={() => join()} disabled={busy || joinCode.length !== 6} className="w-full h-11 rounded-lg bg-brand-blue hover:bg-brand-blue/90 text-white text-xs uppercase tracking-[0.2em] font-black" data-testid="race-join-btn">
              <ArrowRight className="w-4 h-4 mr-2" /> Join race
            </Button>
          </div>
        </div>

        {/* Open rooms */}
        <section>
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mb-6 flex items-center gap-3">
            Open rooms <span className="text-sm font-mono text-muted-foreground font-normal">· {openRaces.length}</span>
          </h2>
          {openRaces.length === 0 ? (
            <div className="border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground font-mono text-sm" data-testid="race-open-empty">
              No open rooms right now. Create one above and share the code with a friend.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {openRaces.map((r) => (
                <button
                  key={r.code}
                  onClick={() => join(r.code)}
                  className="text-left border border-border rounded-2xl p-5 bg-card hover:border-brand-gold/50 hover:-translate-y-0.5 transition-all"
                  data-testid={`race-open-${r.code}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-2xl font-black tracking-widest text-brand-gold">{r.code}</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {r.player_count}/10</div>
                  </div>
                  <div className="text-sm font-bold truncate">{r.passage_title}</div>
                  <div className="text-[11px] font-mono text-muted-foreground mt-1">{r.word_count} words</div>
                </button>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
